// src/components/CallUI.js
//
// Audio / video calls over raw WebRTC (RTCPeerConnection).
// PeerJS is only used as the identity + signaling channel (the peer id "u_<userId>" is
// derived from the logged-in user, so nobody ever types a peer id). Media never goes
// through PeerJS: it flows directly between the two browsers.
//
// <CallUI /> is mounted once (by Navbar.js). Start a call from anywhere with:
//     startCall(userId, 'audio' | 'video', userRecord?)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import pb from '../lib/pb';
import { getPeer } from '../lib/peer';
import { useAuth } from '../contexts/AuthContext';
import { injectStyles } from '../shared/PostCard';
import { FriendAvatar, userName } from './FriendTile';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import VideocamOffRoundedIcon from '@mui/icons-material/VideocamOffRounded';

/* ================================================================== */
/* Shared WebRTC plumbing (FileTransfer.js uses these too)             */
/* ================================================================== */
export function getIceServers() {
  const servers = [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }];
  // optional TURN relay for strict NATs / mobile networks (set these in your .env)
  const turn = process.env.REACT_APP_TURN_URL;
  if (turn) {
    servers.push({
      urls: turn.split(',').map((s) => s.trim()),
      username: process.env.REACT_APP_TURN_USERNAME,
      credential: process.env.REACT_APP_TURN_CREDENTIAL,
    });
  }
  return servers;
}

export const rtcConfig = () => ({
  iceServers: getIceServers(),
  bundlePolicy: 'max-bundle',
  rtcpMuxPolicy: 'require',
  iceCandidatePoolSize: 4,
});

export const peerIdOf = (userId) => `u_${userId}`;
export const userIdOf = (peerId) => String(peerId || '').replace(/^u_/, '').split('_')[0];

/**
 * Signaling hub: one small JSON data connection per user, opened on demand.
 * It carries only SDP / ICE / call-control messages.
 */
export const hub = {
  peer: null,
  conns: new Map(),
  hints: new Map(),
  listeners: new Set(),

  attach(peer) {
    if (!peer || this.peer === peer) return;
    this.peer = peer;
    try {
      peer.on('connection', (c) => { if (c.label === 'signal') this.adopt(c); });
    } catch { /* ignore */ }
  },

  adopt(c) {
    const uid = userIdOf(c.peer);
    this.conns.set(uid, c);
    c.on('data', (d) => this.listeners.forEach((fn) => { try { fn(uid, d); } catch (e) { console.error(e); } }));
    const drop = () => { if (this.conns.get(uid) === c) this.conns.delete(uid); };
    c.on('close', drop);
    c.on('error', drop);
  },

  hint(userId, peerId) {
    if (userId && peerId) this.hints.set(userId, peerId);
  },

  open(userId) {
    const existing = this.conns.get(userId);
    if (existing && existing.open) return Promise.resolve(existing);
    return new Promise((resolve, reject) => {
      const p = this.peer;
      if (!p || p.destroyed || !p.open) { reject(new Error('PEER_NOT_READY')); return; }
      const c = p.connect(this.hints.get(userId) || peerIdOf(userId), { label: 'signal', reliable: true, serialization: 'json' });
      this.adopt(c);
      const t = setTimeout(() => reject(new Error('PEER_UNREACHABLE')), 7000);
      c.on('open', () => { clearTimeout(t); resolve(c); });
      c.on('error', (e) => { clearTimeout(t); reject(e); });
    });
  },

  async send(userId, msg) {
    const c = await this.open(userId);
    c.send(msg);
  },
};

export const sendSignal = (userId, msg) => hub.send(userId, msg);

export function startCall(userId, type = 'audio', user = null) {
  window.dispatchEvent(new CustomEvent('mf-start-call', { detail: { userId, type, user } }));
}

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */
const rid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const stopStream = (s) => { try { s && s.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ } };

const fmtTime = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

// low-latency Opus: in-band FEC, DTX, mono, modest bitrate
function tuneSdp(sdp) {
  const m = sdp.match(/a=rtpmap:(\d+) opus\/48000/i);
  if (!m) return sdp;
  const pt = m[1];
  return sdp.replace(new RegExp(`a=fmtp:${pt} (.*)`), (line, params) => {
    const set = new Map(params.split(';').map((s) => s.trim().split('=')));
    set.set('useinbandfec', '1');
    set.set('usedtx', '1');
    set.set('stereo', '0');
    set.set('maxaveragebitrate', '40000');
    return `a=fmtp:${pt} ${[...set].map(([k, v]) => (v === undefined ? k : `${k}=${v}`)).join(';')}`;
  });
}

function tuneSenders(pc) {
  pc.getSenders().forEach((s) => {
    if (!s.track) return;
    try {
      const p = s.getParameters();
      if (!p.encodings || !p.encodings.length) p.encodings = [{}];
      p.encodings[0].maxBitrate = s.track.kind === 'video' ? 2500000 : 40000;
      if (s.track.kind === 'video') p.degradationPreference = 'maintain-framerate';
      s.setParameters(p).catch(() => {});
    } catch { /* not supported */ }
  });
}

function getMedia(type) {
  return navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
    video: type === 'video'
      ? { width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 }, facingMode: 'user' }
      : false,
  });
}

function friendly(err, name = 'the other person') {
  const n = err && (err.name || err.message);
  if (n === 'NotAllowedError' || n === 'SecurityError') return 'Microphone / camera permission was denied.';
  if (n === 'NotFoundError' || n === 'OverconstrainedError') return 'No microphone / camera was found.';
  if (n === 'PEER_NOT_READY') return 'Still connecting to the network. Try again in a moment.';
  if (n === 'PEER_UNREACHABLE') return `${name} is offline or unreachable.`;
  return 'The call could not be started.';
}

/* ================================================================== */
/* Styles                                                              */
/* ================================================================== */
const CALL_CSS = `
.mfcall{position:fixed;inset:0;z-index:10000;display:flex;flex-direction:column;background:#07090e;color:#fff;
  font-family:inherit;animation:mfcall-in .2s ease-out}
@keyframes mfcall-in{from{opacity:0}to{opacity:1}}
.mfcall-remote{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;background:#000}
.mfcall-remote.is-hidden{width:1px;height:1px;opacity:0;pointer-events:none}
.mfcall-bg{position:absolute;inset:0;background:radial-gradient(900px 500px at 50% 30%,rgba(108,140,255,.22),transparent 70%),#07090e}
.mfcall-top{position:relative;z-index:2;padding:calc(18px + env(safe-area-inset-top)) 18px 8px;text-align:center;
  background:linear-gradient(rgba(0,0,0,.55),transparent)}
.mfcall-top strong{display:block;font-size:18px}
.mfcall-top span{font-size:13.5px;opacity:.85}
.mfcall-center{position:relative;z-index:1;flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;text-align:center;padding:20px}
.mfcall-center h2{margin:0;font-size:clamp(22px,4vw,30px)}
.mfcall-center p{margin:0;font-size:15px;opacity:.85}
.mfcall-pulse{position:relative;display:grid;place-items:center}
.mfcall-pulse::before,.mfcall-pulse::after{content:'';position:absolute;inset:-14px;border-radius:50%;border:2px solid rgba(255,255,255,.28);animation:mfcall-pulse 2s infinite ease-out}
.mfcall-pulse::after{animation-delay:1s}
@keyframes mfcall-pulse{from{transform:scale(.85);opacity:.9}to{transform:scale(1.5);opacity:0}}
.mfcall-pulse.is-still::before,.mfcall-pulse.is-still::after{display:none}
.mfcall-spacer{flex:1}
.mfcall-local{position:absolute;z-index:3;right:14px;top:calc(76px + env(safe-area-inset-top));width:clamp(96px,22vw,180px);aspect-ratio:3/4;object-fit:cover;
  border-radius:16px;background:#111;border:2px solid rgba(255,255,255,.55);box-shadow:0 10px 30px rgba(0,0,0,.5);transform:scaleX(-1)}
.mfcall-bar{position:relative;z-index:2;display:flex;justify-content:center;align-items:center;gap:18px;padding:18px 18px calc(26px + env(safe-area-inset-bottom));
  background:linear-gradient(transparent,rgba(0,0,0,.6))}
.mfcall-btn{width:60px;height:60px;border-radius:50%;border:0;display:grid;place-items:center;cursor:pointer;color:#fff;background:rgba(255,255,255,.16);
  backdrop-filter:blur(8px);transition:transform .12s,background-color .15s}
.mfcall-btn:hover{background:rgba(255,255,255,.26)}
.mfcall-btn:active{transform:scale(.92)}
.mfcall-btn:focus-visible{outline:2px solid #fff;outline-offset:3px}
.mfcall-btn svg{font-size:28px}
.mfcall-btn.is-off{background:#fff;color:#111}
.mfcall-btn.is-end{background:#ef4444}
.mfcall-btn.is-end:hover{background:#dc2626}
.mfcall-btn.is-accept{background:#16a34a}
.mfcall-btn.is-accept:hover{background:#15803d}
.mfcall-lbl{display:flex;flex-direction:column;align-items:center;gap:6px;font-size:12px;opacity:.9}
@media (prefers-reduced-motion:reduce){.mfcall,.mfcall-pulse::before,.mfcall-pulse::after{animation:none!important}}
`;
injectStyles('mf-call-css', CALL_CSS);

/* ================================================================== */
/* Component                                                           */
/* ================================================================== */
export default function CallUI() {
  const { user, peerReady } = useAuth();
  const meRef = useRef(null);
  meRef.current = user;

  const [call, setCall] = useState(null);
  const [, setTick] = useState(0);

  const callRef = useRef(null);
  const pcRef = useRef(null);
  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const offerRef = useRef(null);
  const pendingIce = useRef([]);
  const gateRef = useRef({ ready: false, q: [] });
  const timers = useRef({ noAnswer: null, disc: null });
  const ring = useRef({ ctx: null, timer: null });
  const remoteEl = useRef(null);
  const localEl = useRef(null);

  const put = useCallback((patch) => {
    if (!callRef.current) return;
    callRef.current = { ...callRef.current, ...patch };
    setCall(callRef.current);
  }, []);

  /* ---------- ringtone (generated, no audio files needed) ---------- */
  const stopRing = useCallback(() => {
    clearInterval(ring.current.timer);
    ring.current.timer = null;
  }, []);

  const startRing = useCallback((incoming) => {
    stopRing();
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    try { if (!ring.current.ctx) ring.current.ctx = new AC(); } catch { return; }
    const ctx = ring.current.ctx;
    const beep = () => {
      try {
        if (ctx.state === 'suspended') ctx.resume();
        const t = ctx.currentTime;
        [0, 0.26].forEach((d, i) => {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = 'sine';
          o.frequency.value = incoming ? (i ? 523 : 440) : 425;
          g.gain.setValueAtTime(0.0001, t + d);
          g.gain.exponentialRampToValueAtTime(incoming ? 0.18 : 0.07, t + d + 0.02);
          g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.22);
          o.connect(g);
          g.connect(ctx.destination);
          o.start(t + d);
          o.stop(t + d + 0.24);
        });
      } catch { /* ignore */ }
    };
    beep();
    ring.current.timer = setInterval(beep, incoming ? 2200 : 3200);
  }, [stopRing]);

  /* ---------- tear down ---------- */
  const cleanup = useCallback((note) => {
    stopRing();
    clearTimeout(timers.current.noAnswer);
    clearTimeout(timers.current.disc);
    const pc = pcRef.current;
    pcRef.current = null;
    if (pc) {
      pc.ontrack = null;
      pc.onicecandidate = null;
      pc.onconnectionstatechange = null;
      try { pc.close(); } catch { /* ignore */ }
    }
    stopStream(localRef.current);
    localRef.current = null;
    remoteRef.current = null;
    offerRef.current = null;
    pendingIce.current = [];
    gateRef.current = { ready: false, q: [] };

    if (callRef.current && note) {
      callRef.current = { ...callRef.current, status: 'ended', note };
      setCall(callRef.current);
      const id = callRef.current.id;
      setTimeout(() => {
        if (callRef.current && callRef.current.id === id && callRef.current.status === 'ended') {
          callRef.current = null;
          setCall(null);
        }
      }, 1800);
    } else {
      callRef.current = null;
      setCall(null);
    }
  }, [stopRing]);

  const endCall = useCallback((note, notify = true) => {
    const c = callRef.current;
    if (!c || c.status === 'ended') return;
    if (notify) {
      const t = c.dir === 'in' && c.status === 'incoming' ? 'call-reject' : 'call-end';
      sendSignal(c.userId, { t, callId: c.id }).catch(() => {});
    }
    cleanup(note || 'Call ended');
  }, [cleanup]);

  /* ---------- peer connection ---------- */
  const makePc = useCallback((callId, uid, stream) => {
    const pc = new RTCPeerConnection(rtcConfig());
    gateRef.current = { ready: false, q: [] };

    stream.getTracks().forEach((t) => {
      if (t.kind === 'video') { try { t.contentHint = 'motion'; } catch { /* ignore */ } }
      pc.addTrack(t, stream);
    });

    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      const msg = { t: 'call-ice', callId, candidate: e.candidate.toJSON() };
      if (gateRef.current.ready) sendSignal(uid, msg).catch(() => {});
      else gateRef.current.q.push(msg);
    };

    pc.ontrack = (e) => {
      const rs = remoteRef.current || new MediaStream();
      remoteRef.current = rs;
      if (!rs.getTracks().includes(e.track)) rs.addTrack(e.track);
      try { e.receiver.jitterBufferTarget = 0; } catch { /* not supported */ }
      try { e.receiver.playoutDelayHint = 0; } catch { /* not supported */ }
      setTick((n) => n + 1);
    };

    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (pcRef.current !== pc) return;
      if (s === 'connected') {
        clearTimeout(timers.current.disc);
        clearTimeout(timers.current.noAnswer);
        stopRing();
        if (callRef.current && callRef.current.status !== 'active') put({ status: 'active', startedAt: Date.now() });
      } else if (s === 'failed') {
        endCall('Connection lost', true);
      } else if (s === 'disconnected') {
        clearTimeout(timers.current.disc);
        timers.current.disc = setTimeout(() => {
          if (pcRef.current === pc && pc.connectionState === 'disconnected') endCall('Connection lost', true);
        }, 6000);
      }
    };

    return pc;
  }, [endCall, put, stopRing]);

  const openGate = useCallback((uid) => {
    gateRef.current.ready = true;
    const q = gateRef.current.q;
    gateRef.current.q = [];
    q.forEach((m) => sendSignal(uid, m).catch(() => {}));
  }, []);

  const flushIce = useCallback(async (pc) => {
    const q = pendingIce.current;
    pendingIce.current = [];
    for (const c of q) { try { await pc.addIceCandidate(c); } catch { /* stale candidate */ } }
  }, []);

  /* ---------- outgoing ---------- */
  const begin = useCallback(async (userId, type, userRec) => {
    const me = meRef.current;
    if (!me || !userId || userId === me.id) return;
    if (callRef.current) return; // already in a call
    if (!window.RTCPeerConnection || !navigator.mediaDevices) {
      callRef.current = { id: rid(), userId, user: userRec, type, dir: 'out', status: 'ended', note: 'Calling is not supported in this browser.' };
      setCall(callRef.current);
      setTimeout(() => { callRef.current = null; setCall(null); }, 2200);
      return;
    }

    const id = rid();
    callRef.current = { id, userId, user: userRec || { id: userId }, type, dir: 'out', status: 'calling', muted: false, camOff: false };
    setCall(callRef.current);
    if (!userRec) {
      pb.collection('users').getOne(userId, { requestKey: null, $autoCancel: false })
        .then((u) => { if (callRef.current && callRef.current.id === id) put({ user: u }); }).catch(() => {});
    }
    if (userRec && userRec.peerId) hub.hint(userId, userRec.peerId);

    try {
      const stream = await getMedia(type);
      if (!callRef.current || callRef.current.id !== id) { stopStream(stream); return; }
      localRef.current = stream;

      const pc = makePc(id, userId, stream);
      pcRef.current = pc;
      const offer = await pc.createOffer();
      offer.sdp = tuneSdp(offer.sdp);
      await pc.setLocalDescription(offer);

      await sendSignal(userId, {
        t: 'call-offer', callId: id, type, sdp: pc.localDescription.sdp,
        from: me.id, name: userName(me),
      });
      openGate(userId);
      if (!callRef.current || callRef.current.id !== id) return;
      put({ status: 'ringing' });
      startRing(false);
      timers.current.noAnswer = setTimeout(() => endCall('No answer', true), 45000);
    } catch (err) {
      console.error('Call failed', err);
      if (callRef.current && callRef.current.id === id) cleanup(friendly(err, userName(userRec)));
    }
  }, [cleanup, endCall, makePc, openGate, put, startRing]);

  /* ---------- incoming ---------- */
  const accept = useCallback(async () => {
    const c = callRef.current;
    if (!c || c.dir !== 'in' || c.status !== 'incoming') return;
    stopRing();
    clearTimeout(timers.current.noAnswer);
    put({ status: 'connecting', muted: false, camOff: false });
    try {
      const stream = await getMedia(c.type);
      if (!callRef.current || callRef.current.id !== c.id) { stopStream(stream); return; }
      localRef.current = stream;

      const pc = makePc(c.id, c.userId, stream);
      pcRef.current = pc;
      await pc.setRemoteDescription({ type: 'offer', sdp: offerRef.current });
      await flushIce(pc);
      const answer = await pc.createAnswer();
      answer.sdp = tuneSdp(answer.sdp);
      await pc.setLocalDescription(answer);
      await sendSignal(c.userId, { t: 'call-answer', callId: c.id, sdp: pc.localDescription.sdp });
      openGate(c.userId);
      tuneSenders(pc);
    } catch (err) {
      console.error('Answer failed', err);
      sendSignal(c.userId, { t: 'call-end', callId: c.id }).catch(() => {});
      cleanup(friendly(err, userName(c.user)));
    }
  }, [cleanup, flushIce, makePc, openGate, put, stopRing]);

  /* ---------- signaling ---------- */
  useEffect(() => {
    const onSignal = async (uid, msg) => {
      if (!msg || typeof msg.t !== 'string' || !msg.t.startsWith('call-')) return;
      const c = callRef.current;

      try {
        switch (msg.t) {
          case 'call-offer': {
            if (c) { sendSignal(uid, { t: 'call-busy', callId: msg.callId }).catch(() => {}); return; }
            offerRef.current = msg.sdp;
            pendingIce.current = [];
            const id = msg.callId;
            callRef.current = {
              id, userId: uid, user: { id: uid, name: msg.name }, type: msg.type === 'video' ? 'video' : 'audio',
              dir: 'in', status: 'incoming', muted: false, camOff: false,
            };
            setCall(callRef.current);
            startRing(true);
            timers.current.noAnswer = setTimeout(() => endCall('Missed call', true), 45000);
            pb.collection('users').getOne(uid, { requestKey: null, $autoCancel: false })
              .then((u) => { if (callRef.current && callRef.current.id === id) put({ user: u }); }).catch(() => {});
            break;
          }
          case 'call-answer': {
            const pc = pcRef.current;
            if (!c || c.id !== msg.callId || !pc) return;
            await pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp });
            await flushIce(pc);
            tuneSenders(pc);
            clearTimeout(timers.current.noAnswer);
            stopRing();
            if (callRef.current && callRef.current.status !== 'active') put({ status: 'connecting' });
            break;
          }
          case 'call-ice': {
            if (!c || c.id !== msg.callId) return;
            const pc = pcRef.current;
            if (pc && pc.remoteDescription) { try { await pc.addIceCandidate(msg.candidate); } catch { /* ignore */ } }
            else pendingIce.current.push(msg.candidate);
            break;
          }
          case 'call-reject':
            if (c && c.id === msg.callId) cleanup('Call declined');
            break;
          case 'call-busy':
            if (c && c.id === msg.callId) cleanup('User is busy on another call');
            break;
          case 'call-end':
            if (c && c.id === msg.callId) cleanup(c.status === 'incoming' ? 'Missed call' : 'Call ended');
            break;
          default:
        }
      } catch (err) {
        console.error('Signal error', err);
        if (callRef.current) cleanup('Call failed');
      }
    };

    hub.listeners.add(onSignal);
    return () => { hub.listeners.delete(onSignal); };
  }, [cleanup, endCall, flushIce, put, startRing, stopRing]);

  // attach to the logged-in user's PeerJS identity (retry until it exists)
  useEffect(() => {
    const attach = () => { try { const p = getPeer(); if (p) hub.attach(p); } catch { /* not ready */ } };
    attach();
    const t = setInterval(attach, 2000);
    return () => clearInterval(t);
  }, [peerReady, user && user.id]);

  // start calls from anywhere: window event "mf-start-call" (and the old "openCallUI" from the Friends panel)
  useEffect(() => {
    const onStart = (e) => {
      const d = (e && e.detail) || {};
      begin(d.userId, d.type === 'video' ? 'video' : 'audio', d.user || null);
    };
    const onLegacy = (e) => {
      const d = (e && e.detail) || {};
      const uid = d.userId || userIdOf(d.peerId);
      if (uid) begin(uid, d.type === 'video' ? 'video' : 'audio', d.peerId ? { id: uid, peerId: d.peerId } : null);
    };
    window.addEventListener('mf-start-call', onStart);
    window.addEventListener('openCallUI', onLegacy);
    return () => {
      window.removeEventListener('mf-start-call', onStart);
      window.removeEventListener('openCallUI', onLegacy);
    };
  }, [begin]);

  // elapsed time ticker
  const active = call && call.status === 'active';
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);

  // never leave the camera on
  useEffect(() => () => {
    clearTimeout(timers.current.noAnswer);
    clearTimeout(timers.current.disc);
    stopRing();
    stopStream(localRef.current);
    try { if (pcRef.current) pcRef.current.close(); } catch { /* ignore */ }
    try { if (ring.current.ctx) ring.current.ctx.close(); } catch { /* ignore */ }
  }, [stopRing]);

  // hand the streams to the <video> elements
  useEffect(() => {
    const rv = remoteEl.current;
    if (rv && remoteRef.current && rv.srcObject !== remoteRef.current) {
      rv.srcObject = remoteRef.current;
      const p = rv.play && rv.play();
      if (p && p.catch) p.catch(() => {});
    }
    const lv = localEl.current;
    if (lv && localRef.current && lv.srcObject !== localRef.current) lv.srcObject = localRef.current;
  });

  /* ---------- controls ---------- */
  const toggleMute = () => {
    const s = localRef.current;
    if (!s || !callRef.current) return;
    const next = !callRef.current.muted;
    s.getAudioTracks().forEach((t) => { t.enabled = !next; });
    put({ muted: next });
  };

  const toggleCam = () => {
    const s = localRef.current;
    if (!s || !callRef.current) return;
    const next = !callRef.current.camOff;
    s.getVideoTracks().forEach((t) => { t.enabled = !next; });
    put({ camOff: next });
  };

  /* ---------- UI ---------- */
  if (!call) return null;

  const isVideo = call.type === 'video';
  const name = userName(call.user);
  const status = call.status;
  const showRemoteVideo = isVideo && status === 'active';

  let line = '';
  if (status === 'incoming') line = `Incoming ${isVideo ? 'video' : 'audio'} call`;
  else if (status === 'calling') line = 'Calling…';
  else if (status === 'ringing') line = 'Ringing…';
  else if (status === 'connecting') line = 'Connecting…';
  else if (status === 'active') line = fmtTime(Date.now() - (call.startedAt || Date.now()));
  else if (status === 'ended') line = call.note || 'Call ended';

  const ui = (
    <div className="mfcall" role="dialog" aria-modal="true" aria-label={`${isVideo ? 'Video' : 'Audio'} call with ${name}`}>
      <video ref={remoteEl} className={`mfcall-remote${showRemoteVideo ? '' : ' is-hidden'}`} autoPlay playsInline />
      {!showRemoteVideo && <div className="mfcall-bg" />}

      {showRemoteVideo ? (
        <div className="mfcall-top">
          <strong>{name}</strong>
          <span>{line}</span>
        </div>
      ) : (
        <div className="mfcall-center">
          <div className={`mfcall-pulse${status === 'active' || status === 'ended' ? ' is-still' : ''}`}>
            <FriendAvatar user={call.user} size={132} />
          </div>
          <h2>{name}</h2>
          <p>{line}</p>
        </div>
      )}

      {showRemoteVideo && <div className="mfcall-spacer" />}

      {isVideo && status !== 'incoming' && status !== 'ended' && !call.camOff && (
        <video ref={localEl} className="mfcall-local" autoPlay muted playsInline />
      )}

      <div className="mfcall-bar">
        {status === 'incoming' ? (
          <>
            <div className="mfcall-lbl">
              <button type="button" className="mfcall-btn is-end" onClick={() => endCall('Call declined', true)} aria-label="Decline call">
                <CallEndRoundedIcon />
              </button>
              Decline
            </div>
            <div className="mfcall-lbl">
              <button type="button" className="mfcall-btn is-accept" onClick={accept} aria-label="Accept call">
                {isVideo ? <VideocamRoundedIcon /> : <CallRoundedIcon />}
              </button>
              Accept
            </div>
          </>
        ) : status === 'ended' ? null : (
          <>
            <button
              type="button"
              className={`mfcall-btn${call.muted ? ' is-off' : ''}`}
              onClick={toggleMute}
              aria-pressed={!!call.muted}
              aria-label={call.muted ? 'Unmute microphone' : 'Mute microphone'}
            >
              {call.muted ? <MicOffRoundedIcon /> : <MicRoundedIcon />}
            </button>
            {isVideo && (
              <button
                type="button"
                className={`mfcall-btn${call.camOff ? ' is-off' : ''}`}
                onClick={toggleCam}
                aria-pressed={!!call.camOff}
                aria-label={call.camOff ? 'Turn camera on' : 'Turn camera off'}
              >
                {call.camOff ? <VideocamOffRoundedIcon /> : <VideocamRoundedIcon />}
              </button>
            )}
            <button type="button" className="mfcall-btn is-end" onClick={() => endCall('Call ended', true)} aria-label="End call">
              <CallEndRoundedIcon />
            </button>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(ui, document.querySelector('.mf-feed') || document.body);
}